import { Dialog } from "primereact/dialog";
import styles from "./styles.module.css";
import { Button } from "primereact/button";
import { useEffect, useMemo, useState, useRef } from "react";
import { InputText } from "primereact/inputtext";
import { Checkbox } from "primereact/checkbox";
import { Divider } from "primereact/divider";
import { RadioButton } from "primereact/radiobutton";
import { InputTextarea } from "primereact/inputtextarea";
import { MultiSelect } from "primereact/multiselect";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { departments } from "../../utils/helper/departments";
import { requestGetTalentsCatalog } from "../../services/wisdomServices/talentService";
import { requestUpdateJobs } from "../../services/wisdomServices/jobServices";

const EDITABLE_FIELDS = [
  "jobTitle",
  "jobDescription",
  "department",
  "location",
  "skills",
  "languages",
  "employmentType",
  "offerRate",
  "overview",
  "responsibilities",
  "requirements",
  "nonNegotiables",
  "niceToHaves",
  "benefits",
  "clientContractSigned",
  "requiredCandidates",
  "exams",
  "otherExam",
];

function JobDetails({ job, visible, onHide, onJobUpdated }) {
  const toast = useRef(null);
  const [loading, setLoading] = useState(false);
  const [skills, setSkills] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [formData, setFormData] = useState({
    accountProjectType: "New Project",
    projectName: "",
    jobTitle: "",
    jobDescription: "",
    department: "",
    location: "Remote",
    employmentType: "",
    offerRate: "",
    overview: "",
    responsibilities: "",
    requirements: "",
    nonNegotiables: "",
    niceToHaves: "",
    benefits: "",
    clientContractSigned: false,
    examsRequired: "No",
    exams: [],
    otherExam: "",
    skills: [],
    languages: [],
    requiredCandidates: 0,
    createdBy: ""
  });
  const [initialFormData, setInitialFormData] = useState(null);

  const JobDetailsSkeleton = () => (
    <div style={{ padding: "1rem", width: "600px" }}>
      <Skeleton width="40%" height="20px" className="mb-4" />

      {[1, 2, 3].map((_, i) => (
        <div key={i} style={{ marginBottom: "24px" }}>
          <Skeleton width="30%" height="16px" className="mb-3" />
  
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <Skeleton height="40px" borderRadius="8px" />
            <Skeleton height="80px" borderRadius="8px" />
            <Skeleton height="40px" borderRadius="8px" />
          </div>
        </div>
      ))}
    </div>
  );

  const showError = (message) => {
    toast.current?.show({
      severity: "error",
      summary: "Error",
      detail: message,
      life: 3000,
    });
  };

  const isEmpty = (value) => {
    if (Array.isArray(value)) return value.length === 0;
    return value === null || value === undefined || String(value).trim() === "";
  };

  const validateRequiredFields = () => {
    const requiredFields = [
      ["jobTitle", "Job Title"],
      ["jobDescription", "Job Description"],
      ["department", "Department"],
      ["location", "Type"],
      ["skills", "Skills"],
      ["languages", "Languages"],
      ["employmentType", "Employment Type"],
      ["offerRate", "Offer Rate"],
      ["overview", "Overview"],
      ["responsibilities", "Responsibilities"],
      ["requirements", "Requirements"],
      ["nonNegotiables", "Non-Negotiables"],
      ["niceToHaves", "Nice-To-Haves"],
    ];

    const missingFields = requiredFields.filter(([field]) => isEmpty(formData[field]));

    if (missingFields.length > 0) {
      showError(
        `Please fill in all required fields: ${missingFields
          .map(([, label]) => label)
          .join(", ")}`
      );
      return false;
    }

    if (!formData.requiredCandidates || formData.requiredCandidates < 1) {
      showError("Candidates Required must be at least 1");
      return false;
    }

    return true;
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const onExamsChange = (e) => {
    let updated = [...formData.exams];

    if (e.checked) {
      if (!updated.includes(e.value)) {
        updated.push(e.value);
      }
    } else {
      updated = updated.filter((exam) => exam !== e.value);

      if (e.value === "Other") {
        updateField("otherExam", "");
      }
    }

    updateField("exams", updated);
  };

  const normalizeValue = (value) => {
    if (value === undefined || value === null) return "";
    return value;
  };

  const parseStringToArray = (str) => {
    if (!str) return [];
    return str
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  };

  const mapIdsToValues = (ids, list) => {
    return list.filter((item) => ids.includes(item.id)).map((item) => item.value);
  };

  const buildPatchPayload = (original, updated) => {
    const payload = {};

    Object.keys(updated).forEach((key) => {
      if (key === "catalog") {
        const originalSkills = parseStringToArray(original.skills);
        const originalLanguages = parseStringToArray(original.languages);

        const updatedSkills = mapIdsToValues(updated.catalog?.SKILL || [], skills);
        const updatedLanguages = mapIdsToValues(updated.catalog?.LANGUAGE || [], languages);

        const skillsChanged =
          JSON.stringify(originalSkills.sort()) !== JSON.stringify(updatedSkills.sort());

        const languagesChanged =
          JSON.stringify(originalLanguages.sort()) !== JSON.stringify(updatedLanguages.sort());

        if (skillsChanged || languagesChanged) {
          payload[key] = updated[key];
        }

        return;
      }

      const originalValue = normalizeValue(original[key]);
      const updatedValue = normalizeValue(updated[key]);

      if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
        payload[key] = updated[key];
      }
    });

    return payload;
  };

  const handleUpdateJobOffer = async () => {
    if (!validateRequiredFields()) return;

    try {
      setLoading(true);

      const skillIds = skills
        .filter((skill) => formData.skills.includes(skill.value))
        .map((skill) => skill.id);

      const languageIds = languages
        .filter((lang) => formData.languages.includes(lang.value))
        .map((lang) => lang.id);

      const updatedData = {
        jobTitle: formData.jobTitle,
        jobDescription: formData.jobDescription,
        department: formData.department,
        location: formData.location,
        employmentType: formData.employmentType,
        offerRate: formData.offerRate,
        overview: formData.overview,
        responsibilities: formData.responsibilities,
        requirements: formData.requirements,
        nonNegotiables: formData.nonNegotiables,
        niceToHaves: formData.niceToHaves,
        benefits: formData.benefits,
        clientContractSigned: formData.clientContractSigned,
        requiredCandidates: formData.requiredCandidates,
        catalog: {
          SKILL: skillIds,
          LANGUAGE: languageIds,
        },
      };

      updatedData.assessments =
        formData.examsRequired === "Yes"
          ? formData.exams.join(",")
          : "";

      updatedData.assessmentsOther =
        formData.examsRequired === "Yes" &&
        formData.exams.includes("Other")
          ? formData.otherExam.trim()
          : "";

      const payload = buildPatchPayload(job, updatedData);

      if (Object.keys(payload).length === 0) return;

      const response = await requestUpdateJobs(job.uuid, payload);
      onJobUpdated(response);
      onHide();
    } catch (error) {
      console.error("Error: ", error);
      showError("An error occurred while updating the job.");
    } finally {
      setLoading(false);
    }
  };

  const loadSkills = async () => {
    const response = await requestGetTalentsCatalog("SKILL");
    setSkills(response);
  };

  const loadLanguages = async () => {
    const response = await requestGetTalentsCatalog("LANGUAGE");
    setLanguages(response);
  };

  const dialogHeader = (
    <div className={styles.dialogHeader}>
      <div className={styles.dialogHeaderTitle}>
        <h4>{formData.jobTitle || "Job Details"}</h4>

        <span className={styles.requiredFieldsNotice}>
          (*) Indicates required fields
        </span>
      </div>

      <button
        type="button"
        className={styles.applicationUrlButton}
        onClick={() =>
          window.open(
            `/open-jobs/job/${job.uuid}`,
            "_blank",
            "noopener,noreferrer"
          )
        }
      >
        <i className="pi pi-external-link" />

        <span>Open application page</span>
      </button>
    </div>
  );

  const normalizeLocationType = (value) => {
    if (value === "USA") return "On-site";
    return value || "Remote";
  };

  useEffect(() => {
    loadSkills();
    loadLanguages();
  }, []);

  useEffect(() => {
    if (!job) return;

    const parsedSkills = parseStringToArray(job.skills);
    const parsedLanguages = parseStringToArray(job.languages);
    const parsedExams = parseStringToArray(job.assessments);

    const mappedFormData = {
      accountProjectType: job.accountProjectType || "New Project",
      projectName: job.projectName || job.project?.name || "",
      jobTitle: job.jobTitle || "",
      jobDescription: job.jobDescription || "",
      department: job.department || "",
      location: normalizeLocationType(job.location),
      employmentType: job.employmentType || "Full-time",
      offerRate: job.offerRate || "",
      overview: job.overview || "",
      responsibilities: job.responsibilities || "",
      requirements: job.requirements || "",
      nonNegotiables: job.nonNegotiables || "",
      niceToHaves: job.niceToHaves || "",
      benefits: job.benefits || "",
      clientContractSigned: !!job.clientContractSigned,
      requiredCandidates: job.requiredCandidates || 0,
      skills: parsedSkills,
      languages: parsedLanguages,
      exams: parsedExams,
      examsRequired: parsedExams.length > 0 ? "Yes" : "No",
      otherExam: job.assessmentsOther || "",
      createdBy: job.createdBy || ""
    };

    setFormData(mappedFormData);
    setInitialFormData(mappedFormData);
  }, [job]);

  const hasChanges = useMemo(() => {
    if (!initialFormData) return false;

    return EDITABLE_FIELDS.some((field) => {
      const initialValue = initialFormData[field];
      const currentValue = formData[field];

      if (Array.isArray(initialValue) || Array.isArray(currentValue)) {
        const normalizedInitial = [...(initialValue || [])].sort();
        const normalizedCurrent = [...(currentValue || [])].sort();

        return (
          JSON.stringify(normalizedInitial) !==
          JSON.stringify(normalizedCurrent)
        );
      }

      return String(initialValue ?? "") !== String(currentValue ?? "");
    });
  }, [formData, initialFormData]);

  const isLoading = !job;

  return (
    <Dialog visible={visible} header={dialogHeader} onHide={onHide} dismissableMask>
      <Toast ref={toast} position="top-center" />

      {isLoading ? (
        <JobDetailsSkeleton />
      ) : (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleUpdateJobOffer();
          }}
          onReset={onHide}
          className={styles.jobDetailsForm}
        >
          <div className={styles.jobDetailsDialogBox}>
            <div className={styles.jobRequestForm}>
              <div className={styles.projectDetailsContainer}>
                <div className={styles.projectDetailsContainerTitles}>
                  <h4>Account / Project Details</h4>
                </div>

                <div className={styles.projectOptions}>
                  <div className={styles.projectRadioButtonsBox}>
                    <div className={styles.projectRadioGroup}>
                      <span>Is this a new or current project?</span>

                      <div className={styles.projectRadioButtonsRow}>
                        <div className={styles.projectRadioButtons}>
                          <RadioButton
                            inputId="new-project-option"
                            name="project-option"
                            value="New Project"
                            checked={formData.accountProjectType === "New Project"}
                            disabled
                          />
                          <label htmlFor="new-project-option">New Project</label>
                        </div>

                        <div className={styles.projectRadioButtons}>
                          <RadioButton
                            inputId="existing-project-option"
                            name="project-option"
                            value="Existing Project"
                            checked={formData.accountProjectType === "Existing Project"}
                            disabled
                          />
                          <label htmlFor="existing-project-option">Existing Project</label>
                        </div>
                      </div>
                    </div>

                    <div className={styles.projectRadioGroup}>
                      <span className={styles.projectTypeRadioTitle}>Type *</span>

                      <div className={styles.projectRadioButtonsRow}>
                        <div className={styles.projectRadioButtons}>
                          <RadioButton
                            inputId="remote-location-type"
                            name="location-type"
                            value="Remote"
                            onChange={(e) => updateField("location", e.value)}
                            checked={formData.location === "Remote"}
                          />
                          <label htmlFor="remote-location-type">Remote</label>
                        </div>

                        <div className={styles.projectRadioButtons}>
                          <RadioButton
                            inputId="onsite-location-type"
                            name="location-type"
                            value="On-site"
                            onChange={(e) => updateField("location", e.value)}
                            checked={formData.location === "On-site"}
                          />
                          <label htmlFor="onsite-location-type">On-site</label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.accountOrProjectInputBox}>
                  <label className={styles.inputLabels} htmlFor="account-project-input">
                    Account / Project
                  </label>
                  <InputText
                    id="account-project-input"
                    placeholder="Account / Project Name"
                    style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                    value={formData.projectName}
                    readOnly
                    disabled
                  />
                </div>

                <div className={styles.accountOrProjectInputBox}>
                  <label className={styles.inputLabels} htmlFor="created-by-input">
                    Created By
                  </label>

                  <InputText
                    id="created-by-input"
                    placeholder="Created By"
                    style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                    value={formData.createdBy}
                    readOnly
                    disabled
                  />
                </div>

              </div>

              <div className={styles.projectDetailsContainer}>
                <div className={styles.projectDetailsContainerTitles}>
                  <h4>Job Information *</h4>
                </div>

                <div className={styles.jobInformationInputFields}>
                  <div>
                    <label className={styles.inputLabels} htmlFor="job-title-input">
                      Job Title *
                    </label>

                    <InputText
                      id="job-title-input"
                      value={formData.jobTitle}
                      placeholder="e.g. Senior React Developer"
                      style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                      onChange={(e) => updateField("jobTitle", e.target.value)}
                      required
                    />
                  </div>

                  <div>
                    <label className={styles.inputLabels} htmlFor="job-description-input">
                      Job Description *
                    </label>

                    <InputTextarea
                      id="job-description-input"
                      value={formData.jobDescription}
                      placeholder="Provide a brief description of the role"
                      style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                      onChange={(e) => updateField("jobDescription", e.target.value)}
                      required
                      autoResize
                    />
                  </div>

                  <div>
                    <label className={styles.inputLabels} htmlFor="candidates-required-input">
                      Candidates Required *
                    </label>

                    <InputNumber
                      id="candidates-required-input"
                      value={formData.requiredCandidates}
                      min={1}
                      placeholder="Enter the number of required candidates"
                      style={{
                        width: "100%",
                        backgroundColor: "#FBFAF9",
                        height: "48px",
                        borderColor: "#E7E4DF",
                      }}
                      onValueChange={(e) => updateField("requiredCandidates", e.value)}
                      required
                    />
                  </div>

                  <div className={styles.jobInformationDropdowns}>
                    <div className={styles.jobInformationDropdownsCard}>
                      <label className={styles.inputLabels} htmlFor="department-input">
                        Department *
                      </label>

                      <Dropdown
                        className="w-full"
                        inputId="department-input"
                        value={formData.department}
                        options={departments}
                        optionValue="label"
                        optionLabel="label"
                        placeholder="Select department"
                        onChange={(e) => updateField("department", e.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className={styles.catalogDropdowns}>
                    <div className={styles.catalogDropdownsCard}>
                      <label className={styles.inputLabels} htmlFor="skills-input">
                        Skills *
                      </label>

                      <MultiSelect
                        maxSelectedLabels={3}
                        className="w-full"
                        inputId="skills-input"
                        value={formData.skills}
                        options={skills}
                        optionValue="value"
                        optionLabel="value"
                        placeholder="Select skills"
                        onChange={(e) => updateField("skills", e.value)}
                        required
                      />
                    </div>

                    <div className={styles.catalogDropdownsCard}>
                      <label className={styles.inputLabels} htmlFor="languages-input">
                        Languages *
                      </label>

                      <MultiSelect
                        maxSelectedLabels={3}
                        className="w-full"
                        inputId="languages-input"
                        value={formData.languages}
                        options={languages}
                        optionValue="value"
                        optionLabel="value"
                        placeholder="Select languages"
                        onChange={(e) => updateField("languages", e.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className={styles.jobInformationTypeAndRateInputsContainer}>
                    <div className={styles.jobInformationTypeAndRateInputs}>
                      <div className={styles.jobFormRadioTitles}>
                        <span>Employment Type *</span>
                      </div>

                      <div className={styles.jobInformationTypeAndRateRadioButtonsBox}>
                        <div className={styles.jobInformationTypeAndRateRadioButtons}>
                          <RadioButton
                            inputId="full-time-employment-type-input"
                            name="employment-type"
                            value="Full-time"
                            onChange={(e) => updateField("employmentType", e.value)}
                            checked={formData.employmentType === "Full-time"}
                            required
                          />
                          <label htmlFor="full-time-employment-type-input">Full-time</label>
                        </div>

                        <div className={styles.jobInformationTypeAndRateRadioButtons}>
                          <RadioButton
                            inputId="part-time-employment-type-input"
                            name="employment-type"
                            value="Part-time"
                            onChange={(e) => updateField("employmentType", e.value)}
                            checked={formData.employmentType === "Part-time"}
                            required
                          />
                          <label htmlFor="part-time-employment-type-input">Part-time</label>
                        </div>
                      </div>
                    </div>

                    <div className={styles.jobInformationTypeAndRateInputs}>
                      <label className={styles.inputLabels} htmlFor="offer-rate-input">
                        Offer Rate *
                      </label>

                      <InputText
                        value={formData.offerRate}
                        id="offer-rate-input"
                        placeholder="e.g. $45K - $65K"
                        style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                        onChange={(e) => updateField("offerRate", e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.projectDetailsContainer}>
                <div className={styles.projectDetailsContainerTitles}>
                  <h4>Role Details *</h4>
                </div>

                <div className={styles.roleDetailsInputFields}>
                  <div>
                    <label className={styles.inputLabels} htmlFor="overview-input">
                      Overview *
                    </label>

                    <InputTextarea
                      id="overview-input"
                      value={formData.overview}
                      placeholder="Provide an overview of the role"
                      style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                      onChange={(e) => updateField("overview", e.target.value)}
                      autoResize
                      required
                    />
                  </div>

                  <div>
                    <label className={styles.inputLabels} htmlFor="responsibilities-input">
                      Responsibilities *
                    </label>

                    <InputTextarea
                      id="responsibilities-input"
                      placeholder="List key responsibilities"
                      value={formData.responsibilities}
                      style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                      onChange={(e) => updateField("responsibilities", e.target.value)}
                      autoResize
                      required
                    />
                  </div>

                  <div>
                    <label className={styles.inputLabels} htmlFor="requirements-input">
                      Requirements *
                    </label>

                    <InputTextarea
                      id="requirements-input"
                      placeholder="List job requirements"
                      value={formData.requirements}
                      style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                      onChange={(e) => updateField("requirements", e.target.value)}
                      autoResize
                      required
                    />
                  </div>

                  <div>
                    <label className={styles.inputLabels} htmlFor="non-negotiables-input">
                      Non-Negotiables (From the Client) *
                    </label>

                    <InputTextarea
                      id="non-negotiables-input"
                      placeholder="List client non-negotiables"
                      value={formData.nonNegotiables}
                      style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                      onChange={(e) => updateField("nonNegotiables", e.target.value)}
                      autoResize
                      required
                    />
                  </div>

                  <div>
                    <label className={styles.inputLabels} htmlFor="nice-to-haves-input">
                      Nice-To-Haves *
                    </label>

                    <InputTextarea
                      id="nice-to-haves-input"
                      placeholder="List nice-to-have qualifications"
                      value={formData.niceToHaves}
                      style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                      onChange={(e) => updateField("niceToHaves", e.target.value)}
                      autoResize
                      required
                    />
                  </div>
                </div>
              </div>

              <div className={styles.projectDetailsContainer}>
                <div className={styles.projectDetailsContainerTitles}>
                  <h4>Benefit & Approval</h4>
                </div>

                <div className={styles.roleDetailsInputFields}>
                  <div>
                    <label className={styles.inputLabels} htmlFor="benefits-input">
                      Benefits
                    </label>

                    <InputTextarea
                      id="benefits-input"
                      value={formData.benefits}
                      placeholder="Describe the benefits package"
                      style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                      onChange={(e) => updateField("benefits", e.target.value)}
                      autoResize
                    />
                  </div>

                  <Divider className={styles.formDivider} />

                  <div>
                    <div className={styles.jobFormRadioTitles}>
                      <span>
                        Has the client contract been signed (if a new client) or has
                        client approval been received (if it's a current client)?
                      </span>
                    </div>

                    <div className={styles.isContractSignedInputsBox}>
                      <div className={styles.isContractSignedRadioButton}>
                        <RadioButton
                          inputId="contract-signed-true-input"
                          name="contract-signed"
                          value={true}
                          onChange={(e) => updateField("clientContractSigned", e.value)}
                          checked={formData.clientContractSigned === true}
                        />
                        <label htmlFor="contract-signed-true-input">Yes</label>
                      </div>

                      <div className={styles.isContractSignedRadioButton}>
                        <RadioButton
                          inputId="contract-signed-false-input"
                          name="contract-signed"
                          value={false}
                          onChange={(e) => updateField("clientContractSigned", e.value)}
                          checked={formData.clientContractSigned === false}
                        />
                        <label htmlFor="contract-signed-false-input">No</label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.projectDetailsContainer}>
                <div className={styles.projectDetailsContainerTitles}>
                  <h4>Exams / Assessments</h4>
                  <span className={styles.jobRequestPageText}>
                    Would you like to include any additional assessments for applicants?
                  </span>
                </div>

                <div className={styles.roleDetailsInputFields}>
                  <div className={styles.isContractSignedInputsBox}>
                    <div className={styles.isExamsRequired}>
                      <RadioButton
                        inputId="is-exams-required-true-input"
                        name="exams-required"
                        value="Yes"
                        onChange={(e) => updateField("examsRequired", e.value)}
                        checked={formData.examsRequired === "Yes"}
                      />
                      <label htmlFor="is-exams-required-true-input">Yes</label>
                    </div>

                    <div className={styles.isExamsRequired}>
                      <RadioButton
                        inputId="is-exams-required-false-input"
                        name="exams-required"
                        value="No"
                        onChange={(e) => {
                          updateField("examsRequired", e.value);
                          updateField("exams", []);
                          updateField("otherExam", "");
                        }}
                        checked={formData.examsRequired === "No"}
                      />
                      <label htmlFor="is-exams-required-false-input">No</label>
                    </div>
                  </div>

                  {formData.examsRequired === "Yes" && (
                    <div>
                      <Divider className={styles.formDivider} />

                      <div style={{ marginBottom: "8px" }}>
                        <span>
                          If you wish to include other assessments specific to this role,
                          please specify below.
                        </span>
                      </div>

                      <div className={styles.examsOptions}>
                        <div className={styles.isContractSignedInputsBox}>
                          <div className="flex flex-wrap justify-content-center gap-3">
                            <div className={styles.examsCheckBoxes}>
                              <Checkbox
                                inputId="typing-speed-test"
                                name="exam"
                                value="TypingSpeedTest"
                                onChange={onExamsChange}
                                checked={formData.exams.includes("TypingSpeedTest")}
                              />
                              <label htmlFor="typing-speed-test" className="ml-2">
                                Typing Speed Test
                              </label>
                            </div>

                            <div className={styles.examsCheckBoxes}>
                              <Checkbox
                                inputId="customer-service-simulation"
                                name="exam"
                                value="CustomerServiceSimulation"
                                onChange={onExamsChange}
                                checked={formData.exams.includes("CustomerServiceSimulation")}
                              />
                              <label htmlFor="customer-service-simulation" className="ml-2">
                                Customer Service Simulation
                              </label>
                            </div>

                            <div className={styles.examsCheckBoxes}>
                              <Checkbox
                                inputId="logic-aptitude-test"
                                name="exam"
                                value="LogicAptitudeTest"
                                onChange={onExamsChange}
                                checked={formData.exams.includes("LogicAptitudeTest")}
                              />
                              <label htmlFor="logic-aptitude-test" className="ml-2">
                                Logic / Aptitude Test
                              </label>
                            </div>

                            <div className={styles.examsCheckBoxes}>
                              <Checkbox
                                inputId="role-knowledge-test"
                                name="exam"
                                value="RoleKnowledgeTest"
                                onChange={onExamsChange}
                                checked={formData.exams.includes("RoleKnowledgeTest")}
                              />
                              <label htmlFor="role-knowledge-test" className="ml-2">
                                Role-Specific Knowledge Test
                              </label>
                            </div>
                          </div>
                        </div>

                        <div className={styles.isContractSignedInputsBox}>
                          <div className="flex flex-wrap justify-content-center gap-3">
                            <div className={styles.examsCheckBoxes}>
                              <Checkbox
                                inputId="technical-skills-assessment"
                                name="exam"
                                value="TechnicalSkillsAssessment"
                                onChange={onExamsChange}
                                checked={formData.exams.includes("TechnicalSkillsAssessment")}
                              />
                              <label htmlFor="technical-skills-assessment" className="ml-2">
                                Technical Skills Assessment
                              </label>
                            </div>

                            <div className={styles.examsCheckBoxes}>
                              <Checkbox
                                inputId="communication-skill-test"
                                name="exam"
                                value="CommunicationSkillsTest"
                                onChange={onExamsChange}
                                checked={formData.exams.includes("CommunicationSkillsTest")}
                              />
                              <label htmlFor="communication-skill-test" className="ml-2">
                                Communication Skills Test (Written/Verbal)
                              </label>
                            </div>

                            <div className={styles.examsCheckBoxes}>
                              <Checkbox
                                inputId="data-entry-accuracy-test"
                                name="exam"
                                value="DataEntryAccuracyTest"
                                onChange={onExamsChange}
                                checked={formData.exams.includes("DataEntryAccuracyTest")}
                              />
                              <label htmlFor="data-entry-accuracy-test" className="ml-2">
                                Data Entry Accuracy Test
                              </label>
                            </div>

                            <div className={styles.examsCheckBoxes}>
                              <Checkbox
                                inputId="other"
                                name="exam"
                                value="Other"
                                onChange={onExamsChange}
                                checked={formData.exams.includes("Other")}
                              />
                              <label htmlFor="other" className="ml-2">
                                Other
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>

                      {formData.exams.includes("Other") && (
                        <div className={styles.otherExamInput}>
                          <label htmlFor="other-exam-input" className={styles.inputLabels}>
                            Type in what specific exam do you need the applicant to answer.
                            (You can also paste the link below)
                          </label>

                          <InputText
                            id="other-exam-input"
                            value={formData.otherExam}
                            onChange={(e) => updateField("otherExam", e.target.value)}
                            placeholder="Describe the assessment or paste a link"
                            style={{ width: "100%", backgroundColor: "#FBFAF9" }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className={styles.jobDetaisFooterBtns}>
              <Button
                label="Save"
                icon="pi pi-check"
                loading={loading}
                disabled={loading || !hasChanges}
                type="submit"
                severity="info"
              />

              <Button
                label="Cancel"
                icon="pi pi-times"
                type="reset"
                severity="warning"
              />
            </div>
          </div>
        </form>
      )}
    </Dialog>
  );
}

export default JobDetails;